/**
 * B2BCustomersPage — Phase 52
 *
 * Allows B2B Agents (role = 'agent') to view and edit their assigned clients
 * directly from the B2B Portal.  Stores/Customers (role = 'client') never see
 * this page — an RBAC guard redirects them to /b2b/catalog.
 *
 * Key design decisions:
 *  • Data scoping: fetches only clients where agent_id = session.agentRecord.id
 *    (matches the Phase 40 RLS SELECT policy on inventory_clients).
 *  • Edit modal: identical fields to the ERP InventorySettingsPage customer modal
 *    (Name, Email, Phone, Pricelist, Business ID, B2B Portal Access, VAT liable).
 *  • UPDATE payload: same shape as ERP — no extra fields or columns required.
 *  • company_id is sourced from session.agentRecord.company_id.
 *  • All UI strings are in Hebrew per the B2B portal standard.
 *
 * RLS:
 *  • SELECT: Phase 40 "inventory_clients: agent reads assigned clients"
 *    (agent_id = ANY(ARRAY(SELECT get_agent_id_for_jwt()))).
 *  • UPDATE: Phase 52 "inventory_clients: agent UPDATE own clients"
 *    (agent_id = ANY(ARRAY(SELECT get_agent_id_for_jwt()))).
 *
 * Route: /b2b/customers  (accessible only when session.role === 'agent')
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Store, Pencil, X, Loader as Loader2, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, ShieldAlert, Mail, Phone, Receipt, Building2, ChevronDown, ToggleLeft, ToggleRight, SendHorizontal as SendHorizonal, Briefcase, Search, ReceiptText, User } from 'lucide-react';
import { supabase }   from '@/lib/supabase';
import { useB2BUser } from './B2BUserContext';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Pricelist {
  id:   string;
  name: string;
}

interface InventoryClient {
  id:                       string;
  company_id:               string;
  name:                     string;
  email?:                   string | null;
  phone?:                   string | null;
  pricelist_id:             string | null;
  agent_id:                 string | null;
  consignment_warehouse_id: string | null;
  is_vat_liable:            boolean;
  business_id?:             string | null;
  b2b_allowed?:             boolean;
  client_access?:           string;
  created_at:               string;
  pricelist_name?:          string | null;
  agent_name?:              string | null;
}

// ─── Main component ────────────────────────────────────────────────────────────

export function B2BCustomersPage() {
  const { session } = useB2BUser();
  const navigate    = useNavigate();

  // ── RBAC guard — only agents may access this page ─────────────────────────
  useEffect(() => {
    if (!session) return;          // still loading — wait
    if (session.role !== 'agent') {
      navigate('/b2b/catalog', { replace: true });
    }
  }, [session, navigate]);

  // Derived IDs from session
  const companyId = session?.agentRecord?.company_id ?? null;
  const agentId   = session?.agentRecord?.id         ?? null;
  const isMasterAgent = !!(session?.agentRecord?.is_master_agent)
    || (session?.masterAgentCompanyIds ?? []).includes(companyId ?? '');

  // ── Clients state ─────────────────────────────────────────────────────────
  const [clients,     setClients]     = useState<InventoryClient[]>([]);
  const [custLoading, setCustLoading] = useState(true);
  const [custError,   setCustError]   = useState<string | null>(null);

  // ── Pricelists for the selector ───────────────────────────────────────────
  const [pricelists, setPricelists] = useState<Pricelist[]>([]);

  // ── Modal state ───────────────────────────────────────────────────────────
  const [custModalOpen, setCustModalOpen] = useState(false);
  const [custEditing,   setCustEditing]   = useState<InventoryClient | null>(null);
  const [custModalTab,  setCustModalTab]  = useState<'general' | 'extra'>('general');

  // Form fields (mirrors ERP InventorySettingsPage)
  const [custName,          setCustName]          = useState('');
  const [custEmail,         setCustEmail]         = useState('');
  const [custPhone,         setCustPhone]         = useState('');
  const [custPricelistId,   setCustPricelistId]   = useState<string | null>(null);
  const [custIsVatLiable,   setCustIsVatLiable]   = useState<boolean>(true);
  const [custBusinessId,    setCustBusinessId]    = useState<string>('');
  const [custB2bAllowed,    setCustB2bAllowed]    = useState<boolean>(true);
  const [custClientAccess,  setCustClientAccess]  = useState<string>('none');
  const [custSaving,        setCustSaving]        = useState(false);
  const [custFormError,     setCustFormError]     = useState<string | null>(null);

  // ── Search filter ─────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');

  // ── Toast ─────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ── Fetch clients ─────────────────────────────────────────────────────────
  // RLS SELECT policy (Phase 40) restricts rows to those where agent_id
  // matches the calling agent.  The explicit .eq('agent_id', agentId) is a
  // belt-and-suspenders filter on top of RLS.

  const fetchClients = useCallback(async () => {
    if (!agentId || !companyId) return;
    setCustLoading(true);
    setCustError(null);

    let query = supabase
      .from('inventory_clients')
      .select(`
        id, company_id, name, email, phone, pricelist_id, agent_id,
        consignment_warehouse_id, is_vat_liable, business_id, b2b_allowed, client_access, created_at,
        inventory_pricelists ( name ),
        inventory_agents ( name )
      `);

    if (isMasterAgent) {
      query = query.eq('company_id', companyId).eq('b2b_allowed', true);
    } else {
      query = query.eq('agent_id', agentId);
    }

    const { data, error } = await query.order('name');
    if (error) {
      setCustError(error.message);
      setCustLoading(false);
      return;
    }
    setClients(
      (data ?? []).map((r: any) => ({
        id:                       r.id,
        company_id:               r.company_id,
        name:                     r.name,
        email:                    r.email                 ?? null,
        phone:                    r.phone                 ?? null,
        pricelist_id:             r.pricelist_id          ?? null,
        agent_id:                 r.agent_id              ?? null,
        consignment_warehouse_id: r.consignment_warehouse_id ?? null,
        is_vat_liable:            r.is_vat_liable         ?? true,
        business_id:              r.business_id           ?? null,
        b2b_allowed:              r.b2b_allowed           ?? false,
        client_access:            r.client_access          ?? 'none',
        created_at:               r.created_at,
        pricelist_name:           r.inventory_pricelists?.name ?? null,
        agent_name:               r.inventory_agents?.name ?? null,
      })),
    );
    setCustLoading(false);
  }, [agentId, companyId, isMasterAgent]);

  // ── Fetch pricelists ───────────────────────────────────────────────────────
  const fetchPricelists = useCallback(async () => {
    if (!companyId) return;
    const { data } = await supabase
      .from('inventory_pricelists')
      .select('id, name')
      .eq('company_id', companyId)
      .order('name');
    setPricelists(data ?? []);
  }, [companyId]);

  useEffect(() => {
    fetchClients();
    fetchPricelists();
  }, [fetchClients, fetchPricelists]);

  // ── Modal helpers ──────────────────────────────────────────────────────────

  const openCustEdit = (c: InventoryClient) => {
    setCustEditing(c);
    setCustName(c.name);
    setCustEmail(c.email ?? '');
    setCustPhone(c.phone ?? '');
    setCustPricelistId(c.pricelist_id ?? null);
    setCustIsVatLiable(c.is_vat_liable ?? true);
    setCustBusinessId(c.business_id ?? '');
    setCustB2bAllowed(c.b2b_allowed ?? true);
    setCustClientAccess(c.client_access ?? 'none');
    setCustFormError(null);
    setCustModalTab('general');
    setCustModalOpen(true);
  };

  const closeCustModal = () => {
    setCustModalOpen(false);
    setCustEditing(null);
    setCustName('');
    setCustEmail('');
    setCustPhone('');
    setCustPricelistId(null);
    setCustIsVatLiable(true);
    setCustBusinessId('');
    setCustB2bAllowed(true);
    setCustClientAccess('none');
    setCustFormError(null);
    setCustModalTab('general');
  };

  // Phase 109: reset on close is already handled above

  // ── Validation ─────────────────────────────────────────────────────────────

  const validateCust = (): string | null => {
    if (!custName.trim()) return 'שם הלקוח הוא שדה חובה';
    const em = custEmail.trim();
    const ph = custPhone.trim();
    if (!em && !ph)
      return 'יש למלא לפחות אימייל או טלפון לצורך זיהוי בסיסמה חד-פעמית (OTP)';
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em))
      return 'כתובת אימייל אינה תקינה';
    if (ph && !/^[\d\s\-\+\(\)]{7,15}$/.test(ph))
      return 'מספר טלפון אינו תקין';
    return null;
  };

  // ── Save (UPDATE only — agents edit, not create here) ─────────────────────
  // Phase 52 RLS UPDATE policy: agent_id = ANY(ARRAY(SELECT get_agent_id_for_jwt()))

  const handleCustSave = async () => {
    const err = validateCust();
    if (err) { setCustFormError(err); return; }
    if (!custEditing) return;

    setCustSaving(true);
    setCustFormError(null);

    const payload = {
      name:         custName.trim(),
      email:        custEmail.trim()    || null,
      phone:        custPhone.trim()    || null,
      pricelist_id: custPricelistId     || null,
      is_vat_liable: custIsVatLiable,
      business_id:  custBusinessId.trim() || null,
      b2b_allowed:  custB2bAllowed,
      client_access: custB2bAllowed ? custClientAccess : 'none',
    };

    try {
      const { error } = await supabase
        .from('inventory_clients')
        .update(payload)
        .eq('id', custEditing.id);
      if (error) throw error;
      // Ensure a Supabase Auth user exists for B2B portal access
      if (custB2bAllowed && custClientAccess !== 'none' && (custEmail.trim() || custPhone.trim())) {
        try {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-b2b-auth-user`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              email: custEmail.trim() || undefined,
              phone: custPhone.trim() || undefined,
              name:  custName.trim(),
            }),
          });
        } catch { /* non-fatal */ }
      }
      showToast('פרטי הלקוח עודכנו בהצלחה');
      closeCustModal();
      fetchClients();
    } catch (e) {
      setCustFormError(e instanceof Error ? e.message : 'שגיאה בשמירת הלקוח');
    } finally {
      setCustSaving(false);
    }
  };

  // ── Invite client via WhatsApp (phone) or mailto (email fallback) ─────────

  const handleInviteClient = useCallback(
    (c: InventoryClient) => {
      const b2bUrl  = `${window.location.origin}/b2b`;
      const message = `היי ${c.name}, מצורף קישור למערכת ההזמנות שלנו. לכניסה: ${b2bUrl}`;
      const encoded = encodeURIComponent(message);

      if (c.phone) {
        const digits = c.phone.replace(/[^\d+]/g, '');
        const intl   = digits.startsWith('0') ? `+972${digits.slice(1)}` : digits;
        window.open(
          `https://wa.me/${intl}?text=${encoded}`,
          '_blank',
          'noopener,noreferrer',
        );
      } else if (c.email) {
        const subject = encodeURIComponent('הזמנה למערכת ההזמנות');
        window.location.href = `mailto:${c.email}?subject=${subject}&body=${encoded}`;
      } else {
        showToast('יש להוסיף טלפון או אימייל כדי לשלוח הזמנה', false);
      }
    },
    [showToast],
  );

  // ── Filtered clients ───────────────────────────────────────────────────────

  const filteredClients = searchQuery.trim()
    ? clients.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
        (c.phone?.includes(searchQuery) ?? false) ||
        (c.business_id?.includes(searchQuery) ?? false),
      )
    : clients;

  // ── Render guards ──────────────────────────────────────────────────────────

  if (!session) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] gap-3 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="text-sm">טוען…</span>
      </div>
    );
  }

  if (session.role !== 'agent') return null;

  if (!agentId || !companyId) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12" dir="rtl">
        <div className="flex items-start gap-4 p-5 bg-red-50 border border-red-200 rounded-2xl text-red-700">
          <ShieldAlert className="w-6 h-6 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm">פרטי הסוכן לא נמצאו</p>
            <p className="text-xs mt-1 opacity-80">
              פרופיל הסוכן שלך חסר מזהה חברה או מזהה סוכן. אנא פנה למנהל המערכת.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6" dir="rtl">

      {/* ── Toast ──────────────────────────────────────────────────────────── */}
      {toast && (
        <div
          className={[
            'fixed top-20 right-4 left-4 md:left-auto md:w-80 z-[60]',
            'flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium',
            toast.ok ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
          ].join(' ')}
        >
          {toast.ok
            ? <CheckCircle2 className="w-4 h-4 shrink-0" />
            : <AlertCircle  className="w-4 h-4 shrink-0" />
          }
          <span className="flex-1">{toast.msg}</span>
          <button onClick={() => setToast(null)} className="p-0.5 opacity-70 hover:opacity-100">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Page header ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-teal-100 rounded-xl">
            <Users className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{isMasterAgent ? 'כל הלקוחות' : 'הלקוחות שלי'}</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {isMasterAgent ? 'ניהול כל לקוחות B2B בחברה' : 'ניהול ועריכת לקוחות המשויכים אליך'}
            </p>
          </div>
        </div>

        {/* Search bar */}
        <div className="relative hidden sm:block">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="חיפוש לקוח…"
            className="pr-9 pl-3 py-2 border border-gray-200 rounded-xl text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white
                       w-48 focus:w-64 transition-all duration-200"
          />
        </div>
      </div>

      {/* ── Agent identity banner ───────────────────────────────────────────── */}
      {session.agentRecord && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 border border-orange-200
                        rounded-xl text-xs text-orange-700">
          <Briefcase className="w-3.5 h-3.5 shrink-0" />
          <span>
            מוצג כסוכן: <strong>{session.agentRecord.name}</strong>
            {session.agentRecord.email && (
              <span className="opacity-70"> · {session.agentRecord.email}</span>
            )}
          </span>
        </div>
      )}

      {/* Mobile search */}
      <div className="relative sm:hidden">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="חיפוש לקוח…"
          className="w-full pr-9 pl-3 py-2.5 border border-gray-200 rounded-xl text-sm
                     focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white"
        />
      </div>

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {custError && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200
                        rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {custError}
        </div>
      )}

      {/* ── Clients table ──────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

        {/* Section header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Store className="w-5 h-5 text-teal-500" />
            <div>
              <h2 className="text-base font-semibold text-gray-900">ניהול לקוחות</h2>
              <p className="text-xs text-gray-500">{isMasterAgent ? 'כל לקוחות B2B בחברה' : 'חנויות ונקודות מכירה המשויכות אליך'}</p>
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1
                           bg-teal-50 text-teal-700 text-xs font-semibold rounded-full border border-teal-200">
            <Users className="w-3 h-3" />
            {clients.length} לקוחות
          </span>
        </div>

        {/* Loading */}
        {custLoading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">טוען לקוחות…</span>
          </div>
        ) : clients.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-gray-400">
            <Users className="w-12 h-12 text-gray-200" />
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">אין לקוחות עדיין</p>
              <p className="text-xs text-gray-400 mt-1">
                ניתן להוסיף לקוחות דרך דף בחירת הלקוח
              </p>
            </div>
          </div>
        ) : filteredClients.length === 0 ? (
          /* No search results */
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
            <Search className="w-10 h-10 text-gray-200" />
            <p className="text-sm">לא נמצאו לקוחות עבור "{searchQuery}"</p>
          </div>
        ) : (
          /* Desktop table */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold
                               text-gray-500 uppercase tracking-wide">
                  <th className="px-6 py-3 text-right">שם הלקוח</th>
                  <th className="px-6 py-3 text-right hidden md:table-cell">אימייל</th>
                  <th className="px-6 py-3 text-right hidden sm:table-cell">טלפון</th>
                  {isMasterAgent && <th className="px-6 py-3 text-right hidden lg:table-cell">סוכן</th>}
                  <th className="px-6 py-3 text-right hidden lg:table-cell">מחירון</th>
                  <th className="px-6 py-3 text-right">פעולות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredClients.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50/60 transition-colors">

                    {/* Name */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium text-gray-900">{c.name}</span>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {c.business_id && (
                            <span className="text-[10px] text-gray-400 font-mono" dir="ltr">
                              ח.פ. {c.business_id}
                            </span>
                          )}
                          {c.b2b_allowed && (
                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                              c.client_access === 'full' ? 'bg-green-50 text-green-700'
                              : c.client_access === 'view_only' ? 'bg-amber-50 text-amber-700'
                              : 'bg-gray-100 text-gray-500'
                            }`}>
                              {c.client_access === 'full' ? 'B2B מלא' : c.client_access === 'view_only' ? 'צפייה בלבד' : 'סוכן בלבד'}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Email */}
                    <td className="px-6 py-4 text-gray-500 hidden md:table-cell">
                      {c.email ? (
                        <a
                          href={`mailto:${c.email}`}
                          className="flex items-center gap-1.5 hover:text-blue-600 transition-colors w-fit"
                        >
                          <Mail className="w-3.5 h-3.5 shrink-0" />
                          {c.email}
                        </a>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>

                    {/* Phone */}
                    <td className="px-6 py-4 text-gray-500 hidden sm:table-cell">
                      {c.phone ? (
                        <span className="flex items-center gap-1.5 select-all" dir="ltr">
                          <Phone className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                          {c.phone}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>

                    {/* Agent (master only) */}
                    {isMasterAgent && (
                      <td className="px-6 py-4 hidden lg:table-cell">
                        {c.agent_name ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5
                                           bg-teal-50 text-teal-700 text-xs font-medium rounded-full">
                            <User className="w-3 h-3" />
                            {c.agent_name}
                            {c.agent_id === agentId && (
                              <span className="text-[9px] opacity-70">(אני)</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 italic">לא משויך</span>
                        )}
                      </td>
                    )}

                    {/* Pricelist */}
                    <td className="px-6 py-4 hidden lg:table-cell">
                      {c.pricelist_name ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5
                                         bg-violet-50 text-violet-700 text-xs font-medium rounded-full">
                          <Receipt className="w-3 h-3" />
                          {c.pricelist_name}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">מחיר בסיס</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {/* Invite */}
                        <button
                          onClick={() => handleInviteClient(c)}
                          className={[
                            'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium',
                            'transition-colors',
                            c.phone
                              ? 'text-green-700 bg-green-50 hover:bg-green-100'
                              : c.email
                                ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                                : 'text-gray-400 bg-gray-50 hover:bg-gray-100',
                          ].join(' ')}
                          title={
                            c.phone
                              ? 'שלח הזמנה ב-WhatsApp'
                              : c.email
                                ? 'שלח הזמנה במייל'
                                : 'אין פרטי קשר לשליחה'
                          }
                        >
                          <SendHorizonal className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">הזמן</span>
                        </button>

                        {/* Account / ledger */}
                        <button
                          onClick={() => navigate(`/b2b/account/${c.id}`)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs
                                     font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
                          title="כרטסת לקוח"
                        >
                          <ReceiptText className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">חשבוני</span>
                        </button>

                        {/* Edit */}
                        <button
                          onClick={() => openCustEdit(c)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50
                                     rounded-lg transition-colors"
                          title="ערוך לקוח"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer count */}
        {filteredClients.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-50 text-xs text-gray-400 bg-gray-50/40">
            {filteredClients.length === clients.length
              ? `${clients.length} לקוחות · ערוך לקוח לשינוי מחירון, אימייל, וכו׳`
              : `${filteredClients.length} מתוך ${clients.length} לקוחות`
            }
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          עריכת לקוח — Same modal fields as ERP InventorySettingsPage
          ══════════════════════════════════════════════════════════════════════ */}
      {custModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">

            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h3 className="text-lg font-bold text-gray-900">עריכת לקוח</h3>
              <button
                onClick={closeCustModal}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tab bar */}
            <div className="flex gap-1 px-6 pt-4 shrink-0">
              {(['general', 'extra'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setCustModalTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
                    ${custModalTab === tab
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
                >
                  {tab === 'general' ? 'פרטים כלליים' : 'הגדרות נוספות'}
                </button>
              ))}
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

              {custFormError && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200
                                rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />{custFormError}
                </div>
              )}

              {/* ── TAB: פרטים כלליים ─────────────────────────────────── */}
              {custModalTab === 'general' && (
                <>
                  {/* Name */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">
                      שם הלקוח / החנות <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={custName}
                      onChange={e => setCustName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCustSave()}
                      placeholder="לדוגמה: רשת סופר-פארם סניף הרצליה"
                      autoFocus
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                    />
                  </div>

                  {/* Contact hint */}
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                    יש למלא לפחות <strong>אימייל</strong> או <strong>טלפון</strong> — אחד מהם נדרש לכניסה דרך OTP.
                  </p>

                  {/* Email */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">
                      אימייל
                      <span className="mr-1 text-xs font-normal text-gray-400">(אימייל או טלפון — לפחות אחד)</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="email"
                        value={custEmail}
                        onChange={e => setCustEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCustSave()}
                        placeholder="store@example.com"
                        dir="ltr"
                        className="w-full pr-9 pl-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                   focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                      />
                    </div>
                    <p className="text-xs text-gray-400">ישמש למילוי אוטומטי בשליחת תעודות ולכניסה באפליקציה</p>
                  </div>

                  {/* Phone */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">טלפון</label>
                    <div className="relative">
                      <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="tel"
                        value={custPhone}
                        onChange={e => setCustPhone(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCustSave()}
                        placeholder="050-0000000"
                        dir="ltr"
                        className="w-full pr-9 pl-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                   focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                      />
                    </div>
                    <p className="text-xs text-gray-400">ישמש לכניסה ב-OTP טלפוני באפליקציית ההזמנות</p>
                  </div>

                  {/* Pricelist */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <Receipt className="w-3.5 h-3.5 text-violet-500" />
                      מחירון (לא חובה)
                    </label>
                    <div className="relative">
                      <select
                        value={custPricelistId ?? ''}
                        onChange={e => setCustPricelistId(e.target.value || null)}
                        className="w-full appearance-none px-3 py-2.5 border border-gray-300 rounded-lg
                                   text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-400
                                   focus:border-transparent pr-3 pl-8"
                      >
                        <option value="">— ללא מחירון —</option>
                        {pricelists.map(pl => (
                          <option key={pl.id} value={pl.id}>{pl.name}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4
                                              text-gray-400 pointer-events-none" />
                    </div>
                    {pricelists.length === 0 && (
                      <p className="text-xs text-violet-600">
                        הוסף מחירונים תחת "מחירונים" כדי לשייך ללקוח
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* ── TAB: הגדרות נוספות ──────────────────────────────────── */}
              {custModalTab === 'extra' && (
                <>
                  {/* Business ID */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-indigo-500" />
                      ח.פ. / עוסק מורשה
                      <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
                    </label>
                    <input
                      type="text"
                      value={custBusinessId}
                      onChange={e => setCustBusinessId(e.target.value)}
                      placeholder="לדוגמה: 514123456"
                      dir="ltr"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-400">
                      מספר ח.פ. משמש לקיבוץ סניפים שונים תחת אותו עסק בפורטל ההזמנות
                    </p>
                  </div>

                  {/* B2B Portal Access toggle */}
                  <div
                    className={`flex items-center justify-between px-4 py-3.5 rounded-xl border
                                transition-colors cursor-pointer
                                ${custB2bAllowed
                                  ? 'bg-green-50 border-green-300'
                                  : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setCustB2bAllowed(v => !v)}
                    role="checkbox"
                    aria-checked={custB2bAllowed}
                    tabIndex={0}
                    onKeyDown={e => e.key === ' ' && setCustB2bAllowed(v => !v)}
                  >
                    <div>
                      <p className={`text-sm font-semibold ${custB2bAllowed ? 'text-green-800' : 'text-gray-700'}`}>
                        אפשר גישה לפורטל הזמנות B2B
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        הלקוח משויך למערכת B2B דרך הסוכן
                      </p>
                    </div>
                    {custB2bAllowed
                      ? <ToggleRight className="w-8 h-8 text-green-500 shrink-0" />
                      : <ToggleLeft  className="w-8 h-8 text-gray-300 shrink-0" />
                    }
                  </div>

                  {/* Phase 109: Client access level selector */}
                  {custB2bAllowed && (
                    <div className="space-y-1.5">
                      <label className="block text-sm font-medium text-gray-700">רמת גישה ללקוח</label>
                      <select
                        value={custClientAccess}
                        onChange={e => setCustClientAccess(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                   focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                      >
                        <option value="none">סוכן בלבד — הלקוח לא יכול להתחבר</option>
                        <option value="view_only">צפייה בלבד — הלקוח רואה קטלוג אבל לא מזמין</option>
                        <option value="full">גישה מלאה — הלקוח מתחבר ומזמין בעצמו</option>
                      </select>
                      <p className="text-xs text-gray-400">
                        {custClientAccess === 'none' && 'רק הסוכן יוכל להזמין עבור לקוח זה'}
                        {custClientAccess === 'view_only' && 'הלקוח יוכל לצפות בקטלוג אבל לא להגיש הזמנות'}
                        {custClientAccess === 'full' && 'הלקוח יוכל להתחבר לפורטל ולהגיש הזמנות בעצמו'}
                      </p>
                    </div>
                  )}

                  {/* VAT liable toggle */}
                  <div
                    className={`flex items-center justify-between px-4 py-3.5 rounded-xl border
                                transition-colors cursor-pointer
                                ${custIsVatLiable
                                  ? 'bg-blue-50 border-blue-300'
                                  : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setCustIsVatLiable(v => !v)}
                    role="checkbox"
                    aria-checked={custIsVatLiable}
                    tabIndex={0}
                    onKeyDown={e => e.key === ' ' && setCustIsVatLiable(v => !v)}
                  >
                    <div>
                      <p className={`text-sm font-semibold ${custIsVatLiable ? 'text-blue-800' : 'text-gray-700'}`}>
                        לקוח חייב במע״מ
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        כבה רק עבור לקוחות הפטורים ממע״מ (עמותות, ייצוא וכו׳)
                      </p>
                    </div>
                    {custIsVatLiable
                      ? <ToggleRight className="w-8 h-8 text-blue-500 shrink-0" />
                      : <ToggleLeft  className="w-8 h-8 text-gray-300 shrink-0" />
                    }
                  </div>
                </>
              )}

            </div>{/* end scrollable body */}

            {/* Modal footer */}
            <div className="flex items-center gap-3 justify-end px-6 py-4 border-t border-gray-100 shrink-0">
              <button
                onClick={closeCustModal}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleCustSave}
                disabled={custSaving || !custName.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {custSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                שמור שינויים
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
